import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";

import EachCheck from "./wrapper/EachCheck";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class Check extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleDrawCheck = this.handleDrawCheck.bind(this);
    this.handleEndDrawCheck = this.handleEndDrawCheck.bind(this);
    this.handleDragCheck = this.handleDragCheck.bind(this);
    this.handleDragCheckComplete = this.handleDragCheckComplete.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("checks").bind(this);

    this.state = {};
    this.nodes = [];
  }

  handleDragCheck(index, newValues) {
    this.setState({
      override: {
        index,
        ...newValues,
      },
    });
  }

  handleDragCheckComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { checks } = this.props;
      const newChecks = checks.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              x: override.x,
              y: override.y,
              sx: override.sx !== undefined ? override.sx : each.sx,
              sy: override.sy !== undefined ? override.sy : each.sy,
              angle: override.angle !== undefined ? override.angle : each.angle,
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newChecks, moreProps);
        },
      );
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState(
        {
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    } else {
      this.setState(
        {
          current: {
            x: xyValue[0],
            y: xyValue[1],
            x2: null,
            y2: null,
            sx: 1,
            sy: 1,
            angle: 0,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDrawCheck(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x)) {
      this.setState({
        current: {
          ...current,
          x2: xyValue[0],
          y2: xyValue[1],
        },
      });
    }
  }

  handleEndDrawCheck(xyValue, moreProps, e) {
    const { checks, appearance } = this.props;
    const { current } = this.state;

    if (!isDefined(current) || !isDefined(current.x)) {
      return;
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    // Calculate scale based on drag distance
    const x2 = xyValue[0];
    const y2 = xyValue[1];

    // Default scale if just clicked (no drag)
    let sx = 1;
    let sy = 1;
    let angle = 0;

    // If dragged, calculate scale from pixel distance
    if (current.x2 !== null && current.y2 !== null) {
      const centerX = xScale(current.x);
      const centerY = yScale(current.y);
      const x2Pixel = xScale(x2);
      const y2Pixel = yScale(y2);

      const dx = x2Pixel - centerX;
      const dy = y2Pixel - centerY;

      // CRITICAL: Use same formula as resize handle - baseDim = width, divide by (baseDim / 2)
      const distance = Math.sqrt(dx * dx + dy * dy);
      const baseDim = 8; // dimension[0] from ChartIQ - must match helper method
      if (distance > 5) {
        sx = Math.max(0.2, distance / (baseDim / 2));
        sy = sx; // Keep proportional initially
      }
    }

    const newChecks = [
      ...checks.map((d) => ({ ...d, selected: false })),
      {
        x: current.x,
        y: current.y,
        // Don't store x2/y2 - they're only for initial drawing
        sx,
        sy,
        angle,
        selected: true,
        appearance,
      },
    ];

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newChecks, moreProps, e);
      },
    );
  }

  render() {
    const { appearance, checks, hoverText } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionStrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { current, override } = this.state;

    const tempCheck =
      isDefined(current) && isDefined(current.x) ? (
        <EachCheck
          interactive={false}
          x={current.x}
          y={current.y}
          x2={current.x2}
          y2={current.y2}
          sx={current.sx}
          sy={current.sy}
          angle={current.angle}
          stroke={
            appearance.stroke && typeof appearance.stroke === "object"
              ? appearance.stroke
              : {
                  color: appearance.stroke || "#4CAF50",
                  width: 2,
                  opacity: 1,
                  dash: "Solid",
                }
          }
          fill={
            appearance.fill && typeof appearance.fill === "object"
              ? appearance.fill
              : { color: appearance.fill || "#4CAF50", opacity: 0.6 }
          }
        />
      ) : null;

    return (
      <g>
        {checks.map((each, idx) => {
          const eachAppearance = {
            stroke: each.appearance?.stroke ||
              appearance.stroke || {
                color: "#4CAF50",
                width: 2,
                opacity: 1,
                dash: "Solid",
              },
            fill: each.appearance?.fill ||
              appearance.fill || { color: "#4CAF50", opacity: 0.6 },
          };

          return (
            <EachCheck
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...(idx === override?.index ? override : each)}
              stroke={eachAppearance.stroke}
              fill={eachAppearance.fill}
              onDrag={this.handleDragCheck}
              onDragComplete={this.handleDragCheckComplete}
            />
          );
        })}
        {tempCheck}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionStrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEndDrawCheck}
          onMouseMove={this.handleDrawCheck}
          data-evo-id="interactive-check"
        />
      </g>
    );
  }
}

Check.propTypes = {
  enabled: PropTypes.bool.isRequired,

  snap: PropTypes.bool.isRequired,
  shouldDisableSnap: PropTypes.func.isRequired,
  snapTo: PropTypes.func,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionStrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    fill: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
  }).isRequired,

  checks: PropTypes.array.isRequired,

  hoverText: PropTypes.object.isRequired,
};

Check.defaultProps = {
  snap: false,
  shouldDisableSnap: () => true,
  snapTo: null,

  onStart: noop,
  onComplete: noop,

  currentPositionStroke: "#4CAF50",
  currentPositionOpacity: 1,
  currentPositionStrokeWidth: 2,
  currentPositionRadius: 4,

  checks: [],

  hoverText: {
    enable: false,
    selectedText: "",
    text: "Clique e arraste para criar um check",
    bgHeight: "auto",
    bgWidth: "auto",
  },
};

export default Check;
